import type { LeadCloudFileType } from '@econtractor/types';
import { isCloudFolder, isPathInsideFolder, itemFolderPath, normalizeFolderPath, parentFolderPath } from './folderTree';

export const DROP_CRUMB_ROOT_ID = 'drop:crumb:root';
const DROP_FOLDER_PREFIX = 'drop:folder:';
const DROP_CRUMB_PREFIX = 'drop:crumb:';

export type FileDragKind = 'file' | 'folder';
export type FileDropKind = 'folder' | 'crumb';

export interface FileDragData {
    type: FileDragKind;
    item: LeadCloudFileType;
}

export interface FileDropData {
    type: FileDropKind;
    parentPath: string;
}

export const folderDroppableId = (folderPath: unknown): string =>
    `${DROP_FOLDER_PREFIX}${normalizeFolderPath(folderPath)}`;

export const crumbDroppableId = (path: unknown): string => {
    const normalized = normalizeFolderPath(path);
    return normalized ? `${DROP_CRUMB_PREFIX}${normalized}` : DROP_CRUMB_ROOT_ID;
};

export const itemCurrentParentPath = (item: LeadCloudFileType): string =>
    isCloudFolder(item) ? parentFolderPath(item.folderPath) : itemFolderPath(item);

export const canDropOnParent = (item: LeadCloudFileType | null | undefined, parentPath: unknown): boolean => {
    if (!item) return false;
    const target = normalizeFolderPath(parentPath);
    if (itemCurrentParentPath(item) === target) return false;
    if (isCloudFolder(item)) {
        const oldPath = itemFolderPath(item);
        if (oldPath && isPathInsideFolder(target, oldPath)) return false;
    }
    return true;
};

interface DroppableLike {
    id?: string | number;
    data?: { current?: unknown };
}

export const parentPathFromDroppable = (over: DroppableLike | null | undefined): string | null => {
    if (!over) return null;
    const data = over.data?.current as FileDropData | undefined;
    if (data && (data.type === 'folder' || data.type === 'crumb') && typeof data.parentPath === 'string') {
        return normalizeFolderPath(data.parentPath);
    }

    const id = String(over.id || '');
    if (id === DROP_CRUMB_ROOT_ID) return '';
    if (id.startsWith(DROP_FOLDER_PREFIX)) return normalizeFolderPath(id.slice(DROP_FOLDER_PREFIX.length));
    if (id.startsWith(DROP_CRUMB_PREFIX)) return normalizeFolderPath(id.slice(DROP_CRUMB_PREFIX.length));
    return null;
};

export const itemFromDraggable = (data: unknown): LeadCloudFileType | null => {
    const drag = data as FileDragData | undefined;
    if (drag && (drag.type === 'file' || drag.type === 'folder') && drag.item) return drag.item;
    return null;
};
